import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LuInfo } from 'react-icons/lu';
import './InfoTitulo.css';

/**
 * O "i" ao lado do título de uma tela, com tooltip.
 *
 * Nasceu em Relatórios: a explicação do cálculo era um botão "Como isto é
 * calculado" no cabeçalho, e ele pediu assim — a explicação pertence ao NOME
 * do relatório, não à fileira de ações, que fica só com o que se faz.
 *
 * Abre no passar do mouse e FIXA no clique. Sem o clique, quem chega por toque
 * não alcança o conteúdo, e ler uma lista de definições com o ponteiro parado
 * em cima é desconfortável. Esc e clique fora fecham.
 *
 * O balão vai em PORTAL, preso ao botão. Em Relatórios ele nascia no fluxo e
 * bastava; na ficha do negócio (12/10/2026) o "i" fica dentro de uma coluna de
 * 360px que rola, e o balão era cortado pela borda dela — a mesma razão que
 * levou o Select ao portal. Em `fixed`, quem corta é só a janela.
 */
export default function InfoTitulo({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
    const [aberto, setAberto] = useState(false);
    const [fixo, setFixo] = useState(false);
    const ref = useRef<HTMLSpanElement | null>(null);
    const [caixa, setCaixa] = useState<{ top: number; left: number } | null>(null);

    /* Acompanha o botão: a coluna da ficha rola por baixo, e a página também. */
    useLayoutEffect(() => {
        if (!aberto) { setCaixa(null); return; }
        const posicionar = () => {
            const b = ref.current?.getBoundingClientRect();
            if (!b) return;
            const largura = Math.min(340, window.innerWidth - 24);
            // Encosta na direita da janela em vez de vazar; 12px de respiro.
            const left = Math.max(12, Math.min(b.left - 8, window.innerWidth - largura - 12));
            setCaixa({ top: b.bottom + 8, left });
        };
        posicionar();
        window.addEventListener('scroll', posicionar, true);
        window.addEventListener('resize', posicionar);
        return () => {
            window.removeEventListener('scroll', posicionar, true);
            window.removeEventListener('resize', posicionar);
        };
    }, [aberto]);

    useEffect(() => {
        if (!fixo) return;
        const fora = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) { setFixo(false); setAberto(false); }
        };
        const esc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') { setFixo(false); setAberto(false); }
        };
        document.addEventListener('mousedown', fora);
        document.addEventListener('keydown', esc);
        return () => {
            document.removeEventListener('mousedown', fora);
            document.removeEventListener('keydown', esc);
        };
    }, [fixo]);

    return (
        <span className="rl-info" ref={ref}
            onMouseEnter={() => setAberto(true)}
            onMouseLeave={() => !fixo && setAberto(false)}>
            <button
                type="button"
                className="rl-info-b"
                aria-label={rotulo}
                aria-expanded={aberto}
                onClick={() => { setFixo(f => !f); setAberto(true); }}
            >
                <LuInfo size={15} />
            </button>
            {aberto && caixa && createPortal(
                <span className="rl-info-p" role="tooltip"
                    style={{ top: caixa.top, left: caixa.left }}
                    onMouseEnter={() => setAberto(true)}
                    onMouseLeave={() => !fixo && setAberto(false)}>
                    {children}
                </span>,
                document.body)}
        </span>
    );
}
